import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GlobalContext } from '../contexts/GlobalContext';

/**
 * A higher-order component that checks if a user has access to a page
 * based on completed lessons stored in localStorage.
 *
 * @param {React.Component} Component - The component to render if the user has access
 * @param {string} requiredLesson - The lesson ID that needs to be completed to access this page
 * @param {string} lessonName - The human-readable name of the lesson
 * @param {string} lessonPath - The path to the lesson (not used anymore, redirecting to progress tree)
 * @returns {React.Component} - Either the original component or a message to complete the lesson
 */
const PageAccessWrapper = ({ Component, requiredLesson, lessonName, lessonPath }) => {
  const { completedNodes, userProfile, completedNodesLoading } = useContext(GlobalContext);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Check if the progress tree page, which is always accessible
    if (requiredLesson === 'progress-tree') {
      setHasAccess(true);
      setIsReady(true);
      return;
    }

    // Only proceed when we have all necessary data:
    // 1. Auth context has finished loading
    // 2. User is authenticated
    // 3. We have a userProfile (meaning user data has loaded)
    // 4. completedNodes has finished loading
    // 5. completedNodes is not null/undefined (meaning it has been fetched, even if empty)
    if (
      !authLoading &&
      isAuthenticated &&
      userProfile &&
      !completedNodesLoading &&
      completedNodes !== null &&
      completedNodes !== undefined
    ) {
      console.log('PageAccessWrapper: All data ready, checking access for', requiredLesson);
      console.log('completedNodes:', completedNodes);
      setHasAccess(completedNodes.includes(requiredLesson));
      setIsReady(true);
    } else {
      console.log('PageAccessWrapper: Waiting for data...', {
        authLoading,
        isAuthenticated,
        hasUserProfile: !!userProfile,
        completedNodesLoading,
        completedNodesLength: completedNodes?.length,
        completedNodes: completedNodes,
      });
      setIsReady(false);
    }
  }, [
    requiredLesson,
    completedNodes,
    userProfile,
    isAuthenticated,
    authLoading,
    completedNodesLoading,
  ]);

  // Show loading spinner until we have all necessary data
  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-white p-8 rounded-xl shadow-md max-w-md w-full text-center">
          <div className="mb-6">
            <span className="text-6xl" role="img" aria-label="locked">
              🔒
            </span>
          </div>
          <h2 className="text-2xl font-bold mb-4 text-gray-800">This page is locked</h2>
          <p className="text-gray-600 mb-6">
            Complete the corresponding lesson to unlock this feature.
          </p>
          <Link
            to="/progress-tree"
            className="inline-block px-6 py-3 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-600 transition-colors"
          >
            Go to Lesson Tree
          </Link>
        </div>
      </div>
    );
  }

  return <Component />;
};

export default PageAccessWrapper;
